/**
 * usePersistedSort — a table's sort column and direction, remembered per
 * user until they pick a different one.
 *
 * Every sortable table used to forget its sort the moment the page changed:
 * the office sorted a customer's invoices by balance, opened one, came back,
 * and was looking at them by number again. Corbin asked (2026-10-05) for a
 * clicked sort to "stay that way until they change it next".
 *
 * Saved server-side through `useUserPreference` (key `sort.<tableId>`), so it
 * follows the person to another computer, the same as their toolbar pins.
 * One key per table, not one map of every table, so two tables saving at
 * once can never overwrite each other.
 *
 * Without a `tableId` the hook is plain component state, which is what the
 * tables did before — so a table opts in by naming itself, nothing else.
 */
import { useCallback, useState } from "react";
import { useUserPreference } from "./user-preference";

export type SortDir = "asc" | "desc";

export interface SortState {
    key: string | null;
    dir: SortDir;
}

/** The preference key for one table. Ids are short kebab tokens. */
export function sortPrefKey(tableId: string): string {
    return `sort.${tableId}`;
}

/**
 * A saved sort is only honoured when it still names a column the table has.
 * A column renamed or removed after someone sorted by it would otherwise
 * leave the table "sorted" by nothing, with no header lit to say so.
 */
export function validSavedSort(saved: unknown, validKeys?: readonly string[]): SortState | null {
    if (!saved || typeof saved !== "object") return null;
    const { key, dir } = saved as { key?: unknown; dir?: unknown };
    if (dir !== "asc" && dir !== "desc") return null;
    if (key === null) return { key: null, dir };
    if (typeof key !== "string" || key.length === 0) return null;
    if (validKeys && !validKeys.includes(key)) return null;
    return { key, dir };
}

/**
 * Next sort after a header click: the same column flips direction; a new
 * column starts at `firstDir` (ascending unless the column says otherwise —
 * money and dates usually want biggest / newest first).
 */
export function nextSort(current: SortState, key: string, firstDir: SortDir = "asc"): SortState {
    if (current.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
    return { key, dir: firstDir };
}

export interface UsePersistedSortOptions {
    /** Stable id for this table, e.g. "customer.invoices". Omit to not persist. */
    tableId?: string;
    defaultKey?: string | null;
    defaultDir?: SortDir;
    /** Column keys a saved sort may name; anything else falls back to the default. */
    validKeys?: readonly string[];
}

export interface UsePersistedSortResult {
    sortKey: string | null;
    sortDir: SortDir;
    /** Header click: flip the active column, or switch to a new one. */
    toggleSort: (key: string, firstDir?: SortDir) => void;
    setSort: (next: SortState) => void;
    /**
     * False until the saved sort has been read. A server-sorted list should
     * wait on this before fetching, or it loads the default order and then
     * loads again in the saved one.
     */
    loaded: boolean;
}

export function usePersistedSort(opts: UsePersistedSortOptions = {}): UsePersistedSortResult {
    const { tableId, defaultKey = null, defaultDir = "asc", validKeys } = opts;
    const persisted = Boolean(tableId);
    const [saved, save, { loaded }] = useUserPreference<SortState | null>(sortPrefKey(tableId ?? "_local"), null, {
        enabled: persisted,
    });
    const [local, setLocal] = useState<SortState | null>(null);

    const current: SortState = (persisted ? validSavedSort(saved, validKeys) : local) ?? {
        key: defaultKey,
        dir: defaultDir,
    };

    const setSort = useCallback(
        (next: SortState) => {
            if (persisted) save(next);
            else setLocal(next);
        },
        [persisted, save],
    );

    const toggleSort = (key: string, firstDir?: SortDir) => setSort(nextSort(current, key, firstDir));

    return { sortKey: current.key, sortDir: current.dir, toggleSort, setSort, loaded };
}
